import React, { useEffect, createElement } from 'react';
import { CheckCircle2, XCircle, Star, Zap, FileText, BookOpen, ChevronRight, Settings } from 'lucide-react';
import { Accordion } from './components/Accordion';
import { PurchasePopup } from './components/PurchasePopup';
import { CountdownTimer } from './components/CountdownTimer';
const CHECKOUT_LINK = 'https://checkout.ebookpoint.shop/cart/59741862101073:1';
export function App() {
  useEffect(() => {
    // Meta Pixel Code
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1252242806993118');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  }, []);
  const handleBuyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = CHECKOUT_LINK;
  };
  const modules = [{
    title: '🎯 1. Diagnóstico y Punto de Partida',
    content: 'Evaluación de nivel, objetivos de temporada y errores comunes al empezar. (45-50 pág.)'
  }, {
    title: '🦵 2. Base Física General',
    content: 'Fuerza de tren inferior, core, resistencia cardiovascular y estabilidad de cadera. (50-55 pág.)'
  }, {
    title: '⚡ 3. Fuerza y Potencia Específica',
    content: 'Pliometría, explosividad, trabajo unilateral y transferencia a giros y saltos. (55-60 pág.)'
  }, {
    title: '⚖️ 4. Equilibrio y Propiocepción',
    content: 'Estabilidad de tobillo y rodilla, control postural en pendiente. (45-50 pág.)'
  }, {
    title: '🧘 5. Movilidad Específica',
    content: 'Cadera, tobillo y columna torácica; rutinas pre y post pista. (45-50 pág.)'
  }, {
    title: '🛡️ 6. Prevención de Lesiones',
    content: 'Rodilla (LCA), muñeca, hombro y señales de alerta para frenar a tiempo. (50-55 pág.)'
  }, {
    title: '📅 7. Planificación de Pretemporada',
    content: 'Periodización de 8-12 semanas, progresión de cargas y tapering pre-viaje. (55-60 pág.)'
  }, {
    title: '🍎 8. Nutrición y Recuperación',
    content: 'Hidratación, energía para jornadas largas y sueño en altura. (45-50 pág.)'
  }];
  const bonuses = [{
    title: '📊 Bono 1 — Excel: Seguimiento de entrenamiento',
    content: 'Seguimiento de entrenamiento 8-12 semanas con dashboard, cargas, series y RPE.',
    image: 'https://cdn.magicpatterns.com/uploads/nvak2x2YyLuLzSe7VpXbru/Screenshot_2026-07-04_at_12.53.34_AM.png'
  }, {
    title: '🔥 Bono 2 — PDF: Checklist de calentamiento',
    content: 'Checklist de calentamiento pre-pista (5-10 min).',
    image: 'https://cdn.magicpatterns.com/uploads/pYgXPPk6Aio5Vq613wPZrw/Screenshot_2026-07-04_at_12.53.55_AM.png'
  }, {
    title: '🧘 Bono 3 — PDF: Guía de estiramientos',
    content: 'Guía de estiramientos post-sesión con ilustraciones.',
    image: 'https://cdn.magicpatterns.com/uploads/8oN2Xwa42sRR8vhzBiV1rL/Screenshot_2026-07-04_at_1.10.26_AM.png'
  }, {
    title: '📈 Bono 4 — Excel: Calculadora de carga',
    content: 'Calculadora de carga de entrenamiento semanal con gráfico dinámico.',
    image: 'https://cdn.magicpatterns.com/uploads/3S7TrHNJFvWpwQz2S8sPV3/Screenshot_2026-07-04_at_12.54.26_AM.png'
  }, {
    title: '🥪 Bono 5 — PDF: Guía rápida de nutrición',
    content: 'Guía rápida de nutrición pre/post sesión en frío.',
    image: 'https://cdn.magicpatterns.com/uploads/dJwbNxPRt3NDtrjiWXeWnu/Screenshot_2026-07-04_at_12.54.39_AM.png'
  }];
  const objections = [{
    title: '¿Necesito ir al gimnasio?',
    content: 'No, el plan está diseñado para que puedas hacerlo en tu casa con equipamiento mínimo o nulo.'
  }, {
    title: '¿Sirve si soy principiante?',
    content: '¡Sí! Es ideal para preparar tu cuerpo y evitar las lesiones más comunes de los principiantes.'
  }, {
    title: '¿Cómo recibo el material?',
    content: 'Al realizar el pago, recibirás acceso inmediato a todos los PDFs y Excels en tu correo electrónico.'
  }];
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-sky-200" data-id="element-0">
      <PurchasePopup data-id="element-1" />

      {/* Header / Hero */}
      <header className="relative pt-8 pb-12 px-4 text-center border-b border-sky-100 overflow-hidden" data-id="element-2">
        {/* Blurred snowy Patagonia mountain background */}
        <div className="absolute inset-0 bg-cover bg-center scale-110 blur-md" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=1200&q=80')"
      }} aria-hidden="true" data-id="element-3" />
        {/* Legibility overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/80 to-white/95" aria-hidden="true" data-id="element-4" />
        <div className="relative max-w-md mx-auto" data-id="element-5">
          <span className="inline-flex items-center gap-1.5 bg-sky-100 text-sky-700 text-xs font-black px-3 py-1.5 rounded-full uppercase tracking-wider mb-5" data-id="element-6">
            ❄️ Pretemporada 8-12 semanas
          </span>
          <h1 className="text-[26px] md:text-3xl font-black text-slate-900 leading-[1.15] tracking-tight mb-6" data-id="element-7">
            Esquí Sin Lesiones:{' '}
            <span className="text-sky-600" data-id="element-8">
              llegá a la nieve fuerte y sin miedo a lesionarte
            </span>
          </h1>

          <a href={CHECKOUT_LINK} onClick={handleBuyClick} className="block mb-6 relative group" data-id="element-9">
            <div className="absolute -inset-1 bg-sky-400 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" data-id="element-10"></div>
            <img src="https://cdn.magicpatterns.com/uploads/i6L5ad919to59KDuACoCnD/ChatGPT_Image_Jul_3__2026__11_40_57_PM-removebg-preview.png" alt="Esquí Sin Lesiones Book" className="w-full max-w-sm mx-auto relative z-10 drop-shadow-2xl hover:scale-105 transition-transform duration-300" data-id="element-11" />
          </a>

          <p className="text-lg text-slate-700 font-medium mb-8 leading-relaxed" data-id="element-12">
            Fuerza, equilibrio y prevención de lesiones, todo en un plan
            diseñado para esquiadores y snowboarders reales.
            <br data-id="element-13" />
            <span className="font-bold text-sky-700" data-id="element-14">
              Sin gimnasio complicado, sin excusas.
            </span>
          </p>

          <div className="flex flex-wrap justify-center gap-2.5 mb-8" data-id="element-15">
            <span className="inline-flex items-center gap-1.5 bg-white px-3.5 py-2 rounded-full text-sm font-bold text-slate-700 shadow-sm ring-1 ring-sky-100" data-id="element-16">
              <FileText className="w-4 h-4 text-sky-500" data-id="element-17" /> 100% PDF
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white px-3.5 py-2 rounded-full text-sm font-bold text-slate-700 shadow-sm ring-1 ring-sky-100" data-id="element-18">
              <Zap className="w-4 h-4 text-amber-500" data-id="element-19" /> Entrega Inmediata
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white px-3.5 py-2 rounded-full text-sm font-bold text-slate-700 shadow-sm ring-1 ring-sky-100" data-id="element-20">
              <BookOpen className="w-4 h-4 text-sky-500" data-id="element-21" /> 8 Módulos + 5 Bonos
            </span>
          </div>

          <button onClick={handleBuyClick} className="w-full bg-sky-500 hover:bg-sky-600 text-white text-xl font-black py-5 px-6 rounded-xl shadow-[0_8px_0_rgb(2,132,199)] hover:shadow-[0_4px_0_rgb(2,132,199)] hover:translate-y-1 transition-all flex items-center justify-center gap-2 uppercase tracking-wide" data-id="element-22">
            Quiero esquiar sin lesiones <ChevronRight className="w-6 h-6" data-id="element-23" />
          </button>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-12 px-4 bg-white" data-id="element-24">
        <div className="max-w-md mx-auto" data-id="element-25">
          <h2 className="text-2xl font-bold text-center mb-8 text-slate-800" data-id="element-26">
            Si vas a esquiar, sabes que puede pasar cualquiera de estas cosas:
          </h2>

          <div className="space-y-4 mb-10" data-id="element-27">
            {['🦵 Fatiga extrema en las piernas después de la primera bajada.', '🫁 Falta de aire por la altura y el esfuerzo constante.', '💥 Dolor de rodillas al final del día que te impide disfrutar.', '🤕 Riesgo alto de lesiones (esguinces, roturas) por falta de estabilidad.', '📉 Frustración por no poder seguir el ritmo de tus amigos o familiares.'].map((problem, i) => <div key={i} className="flex items-start gap-3 bg-red-50/50 p-4 rounded-lg border border-red-100" data-id="element-28">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-29" />
                <p className="text-slate-700 font-medium" data-id="element-30">{problem}</p>
              </div>)}
          </div>

          <div className="bg-sky-50 rounded-2xl p-6 text-center border border-sky-100" data-id="element-31">
            <h3 className="text-xl font-bold text-sky-800 mb-6" data-id="element-32">
              Sabemos lo difícil que puede ser (Y doloroso😬) por eso creamos
              Esquí sin lesiones
            </h3>
            <Accordion items={modules} data-id="element-33" />
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-12 px-4 bg-slate-50 border-y border-slate-200" data-id="element-34">
        <div className="max-w-md mx-auto" data-id="element-35">
          <h2 className="text-2xl font-bold text-center mb-8 text-slate-800" data-id="element-36">
            Lo que dicen quienes ya lo probaron
          </h2>

          <div className="space-y-4" data-id="element-37">
            {[{
            name: 'Martín, 34 años',
            text: 'Increíble. Fui a Bariloche y por primera vez no me dolieron las rodillas al segundo día. 100% recomendado.'
          }, {
            name: 'Lucía, 28 años',
            text: 'El plan es súper claro. Lo hice en mi casa sin equipamiento raro y noté la diferencia en la pista al instante.'
          }, {
            name: 'Federico, 41 años',
            text: 'Vale cada peso. Me salvó las vacaciones en San Martín de los Andes. Llegué con mucha más fuerza.'
          }].map((review, i) => <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-slate-100" data-id="element-38">
                <div className="flex gap-1 mb-2" data-id="element-39">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-40" />)}
                </div>
                <p className="text-slate-700 italic mb-3" data-id="element-41">"{review.text}"</p>
                <p className="text-sm font-bold text-slate-900" data-id="element-42">
                  {review.name}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-12 px-4 bg-white" data-id="element-43">
        <div className="max-w-md mx-auto" data-id="element-44">
          <h2 className="text-2xl font-bold text-center mb-8 text-slate-800" data-id="element-45">
            Además, para asegurar la eficiencia de este pack, te damos:
          </h2>

          <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200 mb-8" data-id="element-46">
            <p className="text-center font-black text-amber-600 mb-6 uppercase tracking-wide" data-id="element-47">
              🎁 Estos bonos son GRATIS por el día de hoy
            </p>
            <Accordion items={bonuses} data-id="element-48" />
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8" data-id="element-49">
            <div className="grid grid-cols-2 text-center text-sm font-bold" data-id="element-50">
              <div className="bg-red-500 p-3 text-white border-r border-red-600" data-id="element-51">
                Sin Esquí sin lesiones
              </div>
              <div className="bg-sky-500 p-3 text-white" data-id="element-52">
                Con Esquí sin lesiones
              </div>
            </div>
            <div className="divide-y divide-slate-100 text-sm" data-id="element-53">
              {[['Piernas agotadas al mediodía', 'Energía para todo el día'], ['Miedo constante a caerse', 'Confianza y estabilidad'], ['Dolor articular post-pista', 'Recuperación rápida'], ['Técnica estancada', 'Progresión acelerada'], ['Riesgo alto de lesión', 'Cuerpo preparado y fuerte']].map((row, i) => <div key={i} className="grid grid-cols-2" data-id="element-54">
                  <div className="p-3 text-red-700 border-r border-red-100 flex items-center justify-center text-center bg-red-50" data-id="element-55">
                    <XCircle className="w-4 h-4 text-red-500 mr-1.5 flex-shrink-0" data-id="element-56" />{' '}
                    {row[0]}
                  </div>
                  <div className="p-3 text-sky-900 font-medium flex items-center justify-center text-center bg-sky-50/30" data-id="element-57">
                    <CheckCircle2 className="w-4 h-4 text-sky-500 mr-1.5 flex-shrink-0" data-id="element-58" />{' '}
                    {row[1]}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Final Offer Section */}
      <section className="py-12 px-4 bg-slate-900 text-white" id="compra" data-id="element-59">
        <div className="max-w-md mx-auto" data-id="element-60">
          <h2 className="text-2xl font-black text-center mb-6 text-white" data-id="element-61">
            🎿 Tu acceso completo, hoy
          </h2>

          {/* Unified Offer Card: urgency + summary + price + CTA */}
          <div className="bg-white rounded-2xl text-slate-900 shadow-2xl overflow-hidden ring-4 ring-sky-500/30" data-id="element-62">
            {/* Urgency: countdown + spots */}
            <CountdownTimer data-id="element-63" />

            <div className="p-6" data-id="element-64">
              <p className="text-center text-xs font-black text-sky-600 uppercase tracking-widest mb-1" data-id="element-65">
                Resumen del pedido
              </p>
              <h3 className="text-xl font-black text-center mb-5 text-slate-900" data-id="element-66">
                Esto es todo lo que te llevás
              </h3>

              <ul className="space-y-2.5 mb-5 text-sm font-medium text-slate-700" data-id="element-67">
                <li className="flex justify-between items-center gap-2" data-id="element-68">
                  <span className="flex items-center gap-2" data-id="element-69">
                    <CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0" data-id="element-70" />{' '}
                    Programa completo (8 módulos)
                  </span>
                  <span className="text-slate-400 line-through whitespace-nowrap" data-id="element-71">
                    $25.000
                  </span>
                </li>
                <li className="flex justify-between items-center gap-2" data-id="element-72">
                  <span className="flex items-center gap-2" data-id="element-73">
                    <CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0" data-id="element-74" />{' '}
                    5 Bonos exclusivos
                  </span>
                  <span className="text-slate-400 line-through whitespace-nowrap" data-id="element-75">
                    $33.990
                  </span>
                </li>
                <li className="flex justify-between items-center gap-2" data-id="element-76">
                  <span className="flex items-center gap-2" data-id="element-77">
                    <CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0" data-id="element-78" />{' '}
                    Entrega inmediata por email
                  </span>
                  <span className="text-green-600 font-bold whitespace-nowrap" data-id="element-79">
                    GRATIS
                  </span>
                </li>
                <li className="flex justify-between items-center gap-2" data-id="element-80">
                  <span className="flex items-center gap-2" data-id="element-81">
                    <CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0" data-id="element-82" />{' '}
                    Actualizaciones de por vida
                  </span>
                  <span className="text-green-600 font-bold whitespace-nowrap" data-id="element-83">
                    GRATIS
                  </span>
                </li>
                <li className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-84">
                  <span className="font-bold text-slate-800" data-id="element-85">Valor total</span>
                  <span className="text-slate-400 line-through font-bold" data-id="element-86">
                    $58.990
                  </span>
                </li>
              </ul>

              <div className="bg-sky-50 rounded-xl border border-sky-100 py-4 px-4 mb-5 text-center" data-id="element-87">
                <p className="text-xs text-slate-500 font-bold uppercase mb-1" data-id="element-88">
                  Precio por hoy · pago único
                </p>
                <div className="flex items-end justify-center gap-2" data-id="element-89">
                  <span className="text-5xl font-black text-sky-600 leading-none" data-id="element-90">
                    $19.990
                  </span>
                  <span className="text-sm font-bold text-slate-400 mb-1" data-id="element-91">
                    ARS
                  </span>
                </div>
                <span className="inline-block mt-2 bg-green-100 text-green-700 text-xs font-black px-3 py-1 rounded-full" data-id="element-92">
                  Ahorrás $39.000 (66% OFF)
                </span>
              </div>

              <button onClick={handleBuyClick} className="w-full bg-green-500 hover:bg-green-600 text-white text-xl font-black py-5 px-6 rounded-xl shadow-[0_8px_0_rgb(22,163,74)] hover:shadow-[0_4px_0_rgb(22,163,74)] hover:translate-y-1 transition-all flex items-center justify-center gap-2 uppercase tracking-wide mb-4" data-id="element-93">
                Comprar Ahora <ChevronRight className="w-6 h-6" data-id="element-94" />
              </button>

              <p className="text-center text-xs text-slate-500 font-medium flex items-center justify-center gap-1" data-id="element-95">
                🔒 Pago 100% Seguro y Encriptado
              </p>
            </div>
          </div>

          {/* More Testimonials */}
          <div className="mt-12 space-y-4" data-id="element-96">
            <h3 className="text-xl font-bold text-center mb-6 text-white" data-id="element-97">
              Más esquiadores preparados:
            </h3>
            {[{
            name: 'Diego, 35 años',
            text: 'Siempre volvía de Las Leñas con las piernas destruidas. Este año fue totalmente diferente. ¡Gracias!'
          }, {
            name: 'Valeria, 31 años',
            text: 'Soy de Córdoba y viajo una vez al año a esquiar. Este plan me dio la confianza que me faltaba para no lesionarme.'
          }, {
            name: 'Nico, 29 años',
            text: 'Los ejercicios de propiocepción son la clave. Nunca me sentí tan estable en la tabla de snowboard.'
          }, {
            name: 'Sofía, 38 años',
            text: 'Excelente material. Muy bien explicado y al grano. Los bonos son un golazo.'
          }, {
            name: 'Juan, 42 años',
            text: 'Lo compré dudando pero me sorprendió la calidad. Llegué a la nieve en mi mejor estado físico.'
          }].map((review, i) => <div key={i} className="bg-slate-800 p-5 rounded-xl border border-slate-700" data-id="element-98">
                <div className="flex gap-1 mb-2" data-id="element-99">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" data-id="element-100" />)}
                </div>
                <p className="text-slate-300 text-sm italic mb-2" data-id="element-101">
                  "{review.text}"
                </p>
                <p className="text-xs font-bold text-slate-400" data-id="element-102">
                  {review.name}
                </p>
              </div>)}
          </div>

          {/* FAQ */}
          <div className="mt-12" data-id="element-103">
            <h3 className="text-xl font-bold text-center mb-6 text-white" data-id="element-104">
              Preguntas Frecuentes
            </h3>
            <div className="bg-slate-800 rounded-xl p-1" data-id="element-105">
              <Accordion items={objections} data-id="element-106" />
            </div>
          </div>

          {/* Support */}
          <div className="mt-12 text-center flex flex-col items-center justify-center gap-2 text-slate-400" data-id="element-107">
            <Settings className="w-6 h-6" data-id="element-108" />
            <p className="text-sm" data-id="element-109">Soporte técnico:</p>
            <a href="mailto:EbookDreamShop@gmail.com" className="font-bold text-sky-400 hover:text-sky-300" data-id="element-110">
              EbookDreamShop@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>;
}